import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import uuidV4 from 'uuid/v4';
import {window} from 'ssr-window';
import optimizely from '@optimizely/optimizely-sdk';
import defaultLogger from '@optimizely/optimizely-sdk/lib/plugins/logger';
import {LOG_LEVEL} from '@optimizely/optimizely-sdk/lib/utils/enums';
import Arg from '@spothero/arg';
import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import Config from 'config';

let currentExperiment = null;

const ExperimentUtils = {
    forceVariation: null,

    userAttributes: {},

    init(callback) {
        const variations = StorageUtils.get('sh-experiment-variations', 'cookie');

        currentExperiment = currentExperiment || {};

        if (Config.isBot || currentExperiment.client) {
            if (callback) {
                // Ensure callbacks are initialized
                setTimeout(() => {
                    callback(currentExperiment);
                }, 0);
            }
        } else {
            APIUtils
                .get('/experiments/optimizely-datafile/')
                .then(response => {
                    currentExperiment.client = optimizely.createInstance({
                        datafile: response.data,
                        skipJSONValidation: true,
                        logger: defaultLogger.createLogger({
                            logLevel: LOG_LEVEL.ERROR
                        }),
                    });

                    window.optimizelyClientInstance = currentExperiment.client;
                })
                .catch(error => {
                    console.log(error); // eslint-disable-line no-console

                    currentExperiment = null;
                })
                .then(() => {
                    if (callback) {
                        callback(currentExperiment);
                    }
                });
        }

        // update GA
        if (variations) {
            GTMUtils.push({
                event: 'experimentVariationsUpdated',
                experimentVariations: JSON.stringify(variations)
            });
        }

        if (get(window, 'location.search', false)) {
            const {
                optimizely_x: forceVariation,
            } = Arg.parse(window.location.search);

            this.forceVariation = forceVariation;
        }

        this.initUserAttributes();
    },

    isFeatureEnabled(featureName) {
        if (!Config.isBot && !currentExperiment) {
            throw new Error(`The test client is not initialized, please call init prior to isFeatureEnabled.`);
        }

        if (!featureName) {
            throw new Error(`Missing required argument "featureName".`);
        }

        if (Config.isBot || isUndefined(currentExperiment.client) || Config.runningTests) {
            return false;
        } else {
            return currentExperiment.client.isFeatureEnabled(featureName, this.getId(), this.userAttributes);
        }
    },

    getVariation({experiment, userAttributes = {}} = {}) {
        if (!Config.isBot && !currentExperiment) {
            throw new Error(`The test client is not initialized, please call init prior to getVariation.`);
        }

        if (!experiment) {
            throw new Error(`Missing required argument "experiment".`);
        }

        const variation = (Config.isBot || isUndefined(currentExperiment.client) || Config.runningTests)
            ? 'a'
            : currentExperiment.client.activate(
                experiment,
                this.getId(),
                {
                    ...this.userAttributes,
                    ...userAttributes
                }
            ) || 'a';
        const segmentTraits = {};

        currentExperiment.variations = currentExperiment.variations || {};
        currentExperiment.variations[experiment] = variation;

        // update GA
        GTMUtils.push({
            event: 'experimentVariationsUpdated',
            experimentVariations: JSON.stringify(currentExperiment.variations)
        });

        // update Segment
        for (const key in currentExperiment.variations) {
            if (Object.prototype.hasOwnProperty.call(currentExperiment.variations, key)) {
                segmentTraits[`Experiment ${key}`] = currentExperiment.variations[key];
            }
        }

        if (Config.isDeployed) {
            SegmentUtils.onReady(() => {
                SegmentUtils.identify({
                    uniqueId: SegmentUtils.getId(),
                    traits: segmentTraits
                });
            });
        }

        StorageUtils.set('sh-experiment-variations', currentExperiment.variations, 'cookie', 365);

        return variation;
    },

    getFeatureVariableBoolean({featureName, variableName} = {}) {
        if (!Config.isBot && !currentExperiment) {
            throw new Error(`The test client is not initialized, please call init prior to getFeatureVariableBoolean.`);
        }

        if (!featureName) {
            throw new Error(`Missing required argument "featureName".`);
        }

        if (!variableName) {
            throw new Error(`Missing required argument "variableName".`);
        }

        if (Config.isBot || isUndefined(currentExperiment.client) || Config.runningTests) {
            return false;
        } else {
            return currentExperiment.client.getFeatureVariableBoolean(featureName, variableName, this.getId(), this.userAttributes);
        }
    },

    getExperimentCookie() {
        const shExperimentCookie = StorageUtils.get('sh-experiment-id', 'cookie');

        return (!isNil(shExperimentCookie))
            ? shExperimentCookie.toString()
            : this.createExperimentCookie();
    },

    createExperimentCookie(id) {
        const uuid = id || uuidV4();

        StorageUtils.set('sh-experiment-id', uuid, 'cookie', 365);

        return uuid;
    },

    getId() {
        return this.forceVariation || this.getExperimentCookie();
    },

    initUserAttributes() {
        const userAttributes = {
            client: (EnvironmentUtils.isMobileDevice())
                ? 'mobile-web'
                : 'web',
        };

        if (Config.requestIP) {
            userAttributes.ip = Config.requestIP;
        }

        this.userAttributes = userAttributes;
    },

    setUserAttributes(userAttributes) {
        this.userAttributes = {
            ...this.userAttributes,
            ...userAttributes,
        };
    },
};

export default ExperimentUtils;
